import { transformPreferences } from "./transformPreferences";
import { DateTime } from "luxon";
import { allTimezones } from "react-timezone-select";

const now = DateTime.now();

export const timezoneOptions = Object.entries(allTimezones).map(([value, label]) => {
  const offset = now.setZone(value).offset / 60;    // this way we account the daylight saving time also
  const sign = offset >= 0 ? "+" : "-";
  const formattedOffset = `GMT${sign}${Math.abs(offset)}`;
  return {
    value,
    label: `${value} (${formattedOffset})`,
  };
});

export const customerFields = () => [
  { name: "customer_reference", label: "Reference", type: "text" },
  { name: "display_name", label: "Name", type: "text" },
  { name: "comment", label: "Comment", type: "text" },
];

export const customerDefaultValues = {
  display_name: "Customer",
  customer_reference: null,
  comment: null
};

export const userFields = () => [
  { name: "customer_id", label: "Customer_id", type: "text" },
  { name: "idp_id", label: "Idp_id", type: "text" },
  { name: "email", label: "Email", type: "email" },
  {
    name: "language",
    label: "Language",
    type: "select",
    options: window.appConfig && window.appConfig.languageCodes
  },
  {
    name: "timezone",
    label: "Timezone",
    type: "select",
    options: timezoneOptions,
  }
];

export const userDefaultValues = {
  idp_id: null,
  email: null,
  language: "en",
  timezone: "Europe/Brussels"
};

export const siteFields = () => [
  { name: "customer_id", label: "Customer_id", type: "text" },
  { name: "display_name", label: "Name", type: "text" },
  { name: "postal_address", label: "Postal_address", type: "text" },
  { name: "latitude", label: "Latitude[-90-90°]", type: "text" },
  { name: "longitude", label: "Longitude[-180-180°]", type: "text" },
  {
    name: "timezone",
    label: "Timezone",
    type: "select",
    options: timezoneOptions,
  }
];

export const siteDefaultValues = {
  display_name: "CustomerSite",
  postal_address: null,
  latitude: "37.970833",
  longitude: "23.726110",
  timezone: "Europe/Brussels"
};

const findTypeCfg = (type) =>
  (window.appConfig?.deviceTypes ?? []).find(t => t.value === type);

export const deviceFields = (deviceType = "other") => {
  const typeCfg = findTypeCfg(deviceType);

  const fields = [
    { name: "customer_id", label: "Customer_id", type: "text" },
    { name: "site_id", label: "Site_id", type: "text" },
    {
      name: "device_type",
      label: "Device_type",
      type: "select",
      options: window.appConfig?.deviceTypes
    },
    ...(typeCfg?.brandAndFamily?
        [{
          name: "brand_and_family",
          label: "Brand_and_family",
          type: "select",
          options: typeCfg.brandAndFamily
        }]
      : []),
    { name: "display_name", label: "Name", type: "text" },
    { name: "model", label: "Model", type: "text" },
    { name: "serial_nr", label: "Serial_nr", type: "text" },
    { name: "local_ip", label: "Local_ip", type: "text" },
  ];

  return fields;
};

export const deviceDefaultValues = {
  display_name: "Device",
  device_type: "other",
  brand_and_family: null,
  model: null,
  serial_nr: null,
  local_ip: null
};

export const steeringPreferenceFields = () => [
  { name: "customer_id", label: "Customer_id", type: "text" },
  { name: "site_id", label: "Site_id", type: "text" },
  {
    name: "preference_type",
    label: "Preference_type",
    type: "select",
    options: window.appConfig && window.appConfig.preferenceTypes
  },
  {
    name: "preferences",
    label: "Preferences",
    type: "text",
    transform: (prefs) => JSON.stringify(transformPreferences(prefs), null, 2)
  }
];

export const steeringPreferenceDefaultValues = {
  preference_type: "Idle",
  preferences: "{}"
};

export const marketContextFields = () => [
  { name: "customer_id", label: "Customer_id", type: "text" },
  { name: "site_id", label: "Site_id", type: "text" },
  { name: "system_operator", label: "System_operator", type: "text" },
  { name: "ap_offtake_ean", label: "Ap_offtake_ean", type: "text" },
  { name: "ap_injection_ean", label: "Ap_injection_ean", type: "text" },
  {
    name: "capacity_tariff_type",
    label: "Capacity_tariff_type",
    type: "select",
    options: window.appConfig && window.appConfig.capacityTariffTypes
  },
  {
    name: "energy_tariff_type",
    label: "Energy_tariff_type",
    type: "select",
    options: window.appConfig && window.appConfig.energyTariffTypes
  },
  { name: "capacity_price_param1", label: "Capacity_price_param1", type: "text" },
  { name: "capacity_price_param2", label: "Capacity_price_param2", type: "text" },
  { name: "energy_price_param1", label: "Energy_price_param1", type: "text" },
  { name: "energy_price_param2", label: "Energy_price_param2", type: "text" },
];

export const marketContextDefaultValues = {
  system_operator: null,
  ap_offtake_ean: null,
  ap_injection_ean: null,
  capacity_tariff_type: null,
  energy_tariff_type: null,
  capacity_price_param1: null,
  capacity_price_param2: null,
  energy_price_param1: null,
  energy_price_param2: null
};

export const electricalSystemFields = () => [
  { name: "customer_id", label: "Customer_id", type: "text" },
  { name: "site_id", label: "Site_id", type: "text" },
  { name: "main_amount_of_phases", label: "Main_amount_of_phases", type: "text" },
  { name: "has_grid_measurement", label: "Has_grid_measurement", type: "switch" },
  { name: "has_pv", label: "Has_pv", type: "switch" },
  { name: "pv_azimuth", label: "Pv_azimuth[0-360°]", type: "text" },
  { name: "pv_inclination", label: "Pv_inclination[-90-90°]", type: "text" },
  { name: "has_load_ac_main", label: "Has_load_ac_main", type: "switch" },
  { name: "has_pv_ac_main", label: "Has_pv_ac_main", type: "switch" },
  { name: "has_bess", label: "Has_bess", type: "switch" },
  { name: "has_pv_dc", label: "Has_pv_dc", type: "switch" },
  { name: "has_load_bess_acout1", label: "Has_load_bess_acout1", type: "switch" },
  { name: "has_pv_bess_acout1", label: "Has_pv_bess_acout1", type: "switch" },
  { name: "has_load_bess_acout2", label: "Has_load_bess_acout2", type: "switch" },
  { name: "has_pv_bess_acout2", label: "Has_pv_bess_acout2", type: "switch" },
  { name: "grid_measurement_source", label: "Grid_measurement_source", type: "text" },
  { name: "load_ac_main_measurement_source", label: "Load_ac_main_measurement_source", type: "text" },
  { name: "pv_ac_main_nominal_power", label: "Pv_ac_main_nominal_power [Wp]", type: "text" },
  {
    name: "pv_ac_main_orientation",
    label: "Pv_ac_main_orientation",
    type: "select",
    options: window.appConfig && window.appConfig.orientationTypes
  },
  { name: "pv_ac_measurement_source", label: "Pv_ac_measurement_source", type: "text" },
  { name: "bess_inverter_brand_and_model", label: "Bess_inverter_brand_and_model", type: "text" },
  { name: "bess_amount_of_inverters", label: "Bess_amount_of_inverters", type: "text" },
  { name: "bess_nominal_capacity", label: "Bess_nominal_capacity [Wh]", type: "text" },
  { name: "bess_max_charge_power", label: "Bess_max_charge_power [W]", type: "text" },
  { name: "bess_max_discharge_power", label: "Bess_max_discharge_power [W]", type: "text" },
  { name: "bess_charge_efficiency", label: "Bess_charge_efficiency [0-100%]", type: "text" },
  { name: "bess_discharge_efficiency", label: "Bess_discharge_efficiency [0-100%]", type: "text" },
  { name: "bess_measurement_source", label: "Bess_measurement_source", type: "text" },
  { name: "pv_dc_nominal_power", label: "Pv_dc_nominal_power [Wp]", type: "text" },
  {
    name: "pv_dc_orientation",
    label: "Pv_dc_orientation",
    type: "select",
    options: window.appConfig && window.appConfig.orientationTypes
  },
  { name: "pv_dc_measurement_source", label: "Pv_dc_measurement_source", type: "text" },
  { name: "load_bess_acout1_measurement_source", label: "Load_bess_acout1_measurement_source", type: "text" },
  { name: "pv_bess_acout1_nominal_power", label: "Pv_bess_acout1_nominal_power [Wp]", type: "text" },
  {
    name: "pv_bess_acout1_orientation",
    label: "Pv_bess_acout1_orientation",
    type: "select",
    options: window.appConfig && window.appConfig.orientationTypes
  },
  { name: "pv_bess_acout1_measurement_source", label: "Pv_bess_acout1_measurement_source", type: "text" },
  { name: "load_bess_acout2_measurement_source", label: "Load_bess_acout2_measurement_source", type: "text" },
  { name: "pv_bess_acout2_nominal_power", label: "Pv_bess_acout2_nominal_power [Wp]", type: "text" },
  {
    name: "pv_bess_acout2_orientation",
    label: "Pv_bess_acout2_orientation",
    type: "select",
    options: window.appConfig && window.appConfig.orientationTypes
  },
  { name: "pv_bess_acout2_measurement_source", label: "Pv_bess_acout2_measurement_source", type: "text" },
];

export const electricalSystemDefaultValues = {
  main_amount_of_phases: "1", has_grid_measurement: false, has_pv: false, pv_azimuth: null, pv_inclination: null, has_load_ac_main: false, has_pv_ac_main: false,
  has_bess: false, has_pv_dc: false, has_load_bess_acout1: false, has_pv_bess_acout1: false, has_load_bess_acout2: false,
  has_pv_bess_acout2: false, grid_measurement_source: null, load_ac_main_measurement_source: null, pv_ac_main_nominal_power: null,
  pv_ac_main_orientation: "Other", pv_ac_measurement_source: null, bess_inverter_brand_and_model: null, bess_amount_of_inverters: null,
  bess_nominal_capacity: null, bess_max_charge_power: null, bess_max_discharge_power: null, bess_charge_efficiency: null,
  bess_discharge_efficiency: null, bess_measurement_source: null, pv_dc_nominal_power: null, pv_dc_orientation: "Other",
  pv_dc_measurement_source: null, load_bess_acout1_measurement_source: null, pv_bess_acout1_nominal_power: null,
  pv_bess_acout1_orientation: "Other", pv_bess_acout1_measurement_source: null, load_bess_acout2_measurement_source: null,
  pv_bess_acout2_nominal_power: null, pv_bess_acout2_orientation: "Other", pv_bess_acout2_measurement_source: null
};